import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
// SearchProvider removed - search functionality moved to individual sections
import { UserRolesProvider } from './contexts/UserRolesContext';
import { LocalizationProvider } from './contexts/LocalizationContext';
import { SystemPresetProvider } from './contexts/SystemPresetContext';
import { CacheProvider } from './contexts/CacheContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
// Import admin utilities for console access
import './utils/adminUtils';
import './utils/debugSubscription';
import DashboardLayout from './components/layout/DashboardLayout';
import Statistics from './pages/statistics/Statistics';
import MyOffices from './pages/my-offices/MyOffices';
import MyClients from './pages/clients/MyClients';
import Events from './pages/events/Events';
import LegalFiles from './pages/legal/LegalFiles';
import TeamMembers from './pages/team-members/TeamMembers';
import LawyerGuide from './pages/lawyer-guide/LawyerGuide';
import Profile from './pages/profile/Profile';
import Support from './pages/support/Support';
import ReferralProgram from './pages/referral-program/ReferralProgram';
import Community from './pages/community/Community';
import Subscriptions from './pages/subscriptions/Subscriptions';
import PaymentPage from './pages/payment/PaymentPage';
import PaymentSuccessPage from './pages/payment/PaymentSuccessPage';
import PaymentFailurePage from './pages/payment/PaymentFailurePage';
import PaymentResultBridgePage from './pages/payment/PaymentResultBridgePage';
import BillPage from './pages/bill/BillPage';
import TermsAndConditions from './pages/content/TermsAndConditions';
import PrivacyPolicy from './pages/content/PrivacyPolicy';
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import LoadingPage from './pages/auth/LoadingPage';
import GoogleCreateAccount from './pages/auth/GoogleCreateAccount';
import DemographicsSetup from './pages/auth/DemographicsSetup';
import ProfilePhotoSetup from './pages/auth/ProfilePhotoSetup';
import LandingPage from './pages/content/LandingPage/LandingPage';
import DownloadAndroidPage from './pages/content/DownloadAndroidPage';
import Invite from './pages/content/Invite';
import { initializeMetaPixel, trackPageView } from './services/metaPixelService';
import { initializeAdSense } from './utils/adsense';
import { isDemographicsComplete } from './utils/googleAuth';

// Loading Component
const LoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-primary/10 flex items-center justify-center">
    <div className="text-center">
      <div className="spinner h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Meta Pixel Page View Tracker
const PageViewTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Initialize Meta Pixel on first load
    initializeMetaPixel();
    // Initialize AdSense on first load
    initializeAdSense();
  }, []);

  useEffect(() => {
    // Track page view on route change
    trackPageView();
  }, [location.pathname]);

  return null;
};

// Protected Route Component — also enforces demographics gate (OW-184 / OW-185)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  const path = location.pathname;
  const authFunnel =
    path.startsWith('/auth/') || path === '/loading';
  if (user && !authFunnel && !isDemographicsComplete(user)) {
    return <Navigate to="/auth/demographics" replace />;
  }

  return <>{children}</>;
};

// Simple navigation helper - no longer needed with LoadingPage


function AppContent() {
  const { user, isLoggedIn, loading } = useAuth();
  const isDownloadAndroidPath =
    typeof window !== 'undefined' && window.location.pathname === '/download-android';

  // Public Route Component (redirect to loading page if already logged in, unless showing trial modal)
  const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (loading) {
      return <LoadingScreen />;
    }

    const path = window.location.pathname;
    if (isLoggedIn && user && path !== '/signup' && !path.startsWith('/auth/')) {
      if (!isDemographicsComplete(user)) {
        return <Navigate to="/auth/demographics" replace />;
      }
      return <Navigate to="/loading" replace />;
    }
    
    // If user is on signup page and logged in, send them to demographics funnel
    if (isLoggedIn && user && path === '/signup') {
      return <Navigate to="/auth/demographics" replace />;
    }
    
    return <>{children}</>;
  };

  // Open Route Component (accessible to both logged-in and non-logged-in users)
  const OpenRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (loading) {
      return <LoadingScreen />;
    }

    return <>{children}</>;
  };

  // Role-based Protected Route Component for Lawyers
  const LawyerProtectedRoute: React.FC<{ 
    children: React.ReactNode; 
    allowedRoles: string[] 
  }> = ({ children, allowedRoles }) => {
    if (loading) {
      return <LoadingScreen />;
    }

    if (!user || !allowedRoles.includes(user.role)) {
      return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
  };
  
  // Auth gate blocks the whole router — skip for paid-ads Play handoff so Meta IAB is not stuck on Loading.
  if (loading && !isDownloadAndroidPath) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <PageViewTracker />
      <Routes>
            {/* Landing Page - Public */}
            <Route path="/" element={<LandingPage />} />

            {/* Paid ads → log UTM → Play Install Referrer (must stay public; never send to /) */}
            <Route path="/download-android" element={<DownloadAndroidPage />} />

            {/* Peer invite hub — OW-513 / RF-006 */}
            <Route path="/invite" element={<Invite />} />
            
            {/* Public Routes */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/signup" element={
              <PublicRoute>
                <SignUp />
              </PublicRoute>
            } />
            <Route path="/auth/google-create-account" element={<GoogleCreateAccount />} />
            <Route path="/auth/demographics" element={
              <ProtectedRoute>
                <DemographicsSetup />
              </ProtectedRoute>
            } />
            <Route path="/auth/profile-photo" element={
              <ProtectedRoute>
                <ProfilePhotoSetup />
              </ProtectedRoute>
            } />
            <Route path="/terms-and-conditions" element={
              <OpenRoute>
                <TermsAndConditions />
              </OpenRoute>
            } />
            <Route path="/privacy-policy" element={
              <OpenRoute>
                <PrivacyPolicy />
              </OpenRoute>
            } />

            {/* Loading Page - Transition between login and dashboard */}
            <Route path="/loading" element={
              <ProtectedRoute>
                <LoadingPage />
              </ProtectedRoute>
            } />

          {/* Protected Routes with Dashboard Layout */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard/events" replace />} />
            <Route path="statistics" element={<Statistics />} />
            <Route path="my-offices" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <MyOffices />
              </LawyerProtectedRoute>
            } />
            <Route path="my-clients" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <MyClients />
              </LawyerProtectedRoute>
            } />
            <Route path="events" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <Events />
              </LawyerProtectedRoute>
            } />
            <Route path="legal-files" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <LegalFiles />
              </LawyerProtectedRoute>
            } />
            <Route path="team-members" element={
              <LawyerProtectedRoute allowedRoles={['lawyer']}>
                <TeamMembers />
              </LawyerProtectedRoute>
            } />
            <Route path="lawyer-guide" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <LawyerGuide />
              </LawyerProtectedRoute>
            } />
            <Route path="community" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <Community />
              </LawyerProtectedRoute>
            } />
            <Route path="subscriptions" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <Subscriptions />
              </LawyerProtectedRoute>
            } />
            <Route path="profile" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <Profile />
              </LawyerProtectedRoute>
            } />
            <Route path="referral" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <ReferralProgram />
              </LawyerProtectedRoute>
            } />
            <Route path="support" element={
              <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
                <Support />
              </LawyerProtectedRoute>
            } />
          </Route>

          {/* Kashier Ad-Free return — public; must be before payment/:planId */}
          <Route path="/payment/result" element={
            <OpenRoute>
              <PaymentResultBridgePage />
            </OpenRoute>
          } />

          {/* Standalone Payment and Bill Pages */}
          <Route path="payment/:planId" element={
            <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
              <PaymentPage />
            </LawyerProtectedRoute>
          } />
          <Route path="payment/success" element={
            <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
              <PaymentSuccessPage />
            </LawyerProtectedRoute>
          } />
          <Route path="payment/failure" element={
            <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
              <PaymentFailurePage />
            </LawyerProtectedRoute>
          } />
          <Route path="bill/:transactionId" element={
            <LawyerProtectedRoute allowedRoles={['lawyer', 'office_member']}>
              <BillPage />
            </LawyerProtectedRoute>
          } />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
    </Router>
  );
}

// Wrapper component to provide user ID to UserRolesProvider
const AppWithProviders: React.FC = () => {
  const { user } = useAuth();
  
  return (
    <UserRolesProvider userId={user?.id || undefined}>
      <CacheProvider>
        <SubscriptionProvider>
          <AppContent />
        </SubscriptionProvider>
      </CacheProvider>
    </UserRolesProvider>
  );
};

function App() {
  return (
    <LocalizationProvider>
      <ThemeProvider>
        <AuthProvider>
          <SystemPresetProvider>
            <AppWithProviders />
          </SystemPresetProvider>
        </AuthProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
